import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

export default function PrivacyPolicy() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-white hover:text-yellow-200 transition-colors text-sm"
      >
        Privatlivspolitik
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 overflow-y-auto"
            onClick={() => setIsOpen(false)}
          >
            <div 
              className="max-w-4xl mx-auto p-6 my-10"
              onClick={e => e.stopPropagation()}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="bg-white rounded-lg shadow-xl p-8 text-gray-900"
              >
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Privatlivspolitik for Benny's Pølsevogn
                  </h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                <div className="prose max-w-none prose-headings:text-gray-900 prose-p:text-gray-900 space-y-6">
                  <p>
                    Hos Benny's Pølsevogn ("vi", "vores" eller "os") er vi dedikerede til at beskytte dine personoplysninger og sikre, at din oplevelse på vores hjemmeside, bennyspolsevogn.dk ("hjemmesiden"), er sikker og tilfredsstillende. Denne privatlivspolitik beskriver, hvordan vi indsamler, bruger, opbevarer og beskytter dine oplysninger, når du besøger vores hjemmeside eller interagerer med os.
                  </p>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">1. Hvem er ansvarlig for dine personoplysninger?</h3>
                  <p>
                    Benny's Pølsevogn er dataansvarlig for de personoplysninger, der indsamles via vores hjemmeside. Hvis du har spørgsmål om vores behandling af dine data, kan du kontakte os:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Adresse:</strong> Brogårdsvej 14, 2820 Gentofte, Danmark</li>
                    <li><strong>E-mail:</strong> <a href="mailto:brian@bennyspolsevogn.dk" className="text-red-600 hover:text-red-700">brian@bennyspolsevogn.dk</a></li>
                    <li><strong>Telefon:</strong> +45 28 19 50 77</li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">2. Hvilke oplysninger indsamler vi?</h3>
                  <p>Vi kan indsamle følgende typer af oplysninger, når du besøger vores hjemmeside eller benytter vores tjenester:</p>
                  
                  <ol className="list-decimal pl-6 space-y-4">
                    <li>
                      <strong>Oplysninger, du giver os direkte:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>Navn, e-mailadresse og telefonnummer (fx når du udfylder en kontaktformular eller foretager en bestilling).</li>
                        <li>Leveringsadresse og betalingsoplysninger (hvis du bestiller mad online).</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Automatisk indsamlede oplysninger:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>IP-adresse, browsertype, enhedstype og operativsystem.</li>
                        <li>Oplysninger om din brug af hjemmesiden, fx hvilke sider du besøger og tidspunktet for dit besøg.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Cookies og lignende teknologier:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>Vi indsamler ikke cookies på vores hjemmeside.</li>
                      </ul>
                    </li>
                  </ol>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">3. Hvorfor indsamler vi dine oplysninger?</h3>
                  <p>Vi bruger dine oplysninger til følgende formål:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>For at levere vores tjenester:</strong>
                      <br />
                      Vi bruger dine oplysninger til at behandle og levere bestillinger, svare på dine forespørgsler og sikre en god kundeservice.
                    </li>
                    <li>
                      <strong>For at forbedre vores hjemmeside:</strong>
                      <br />
                      Vi analyserer brugeradfærd for at optimere funktionaliteten og designet af vores hjemmeside.
                    </li>
                    <li>
                      <strong>For markedsføringsformål:</strong>
                      <br />
                      Hvis du har givet dit samtykke, sender vi dig nyhedsbreve og tilbud om vores produkter og tjenester.
                    </li>
                    <li>
                      <strong>For at overholde lovgivningen:</strong>
                      <br />
                      Vi opbevarer og behandler dine data i henhold til gældende lovgivning, herunder bogføringsloven og GDPR.
                    </li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">4. Hvordan beskytter vi dine oplysninger?</h3>
                  <p>
                    Vi tager dine data alvorligt og har implementeret passende tekniske og organisatoriske sikkerhedsforanstaltninger for at beskytte dine oplysninger mod uautoriseret adgang, tab, misbrug eller ødelæggelse. Nogle af vores tiltag inkluderer:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Kryptering af data under overførsler (SSL-certifikater).</li>
                    <li>Regelmæssig sikkerhedsovervågning af vores systemer.</li>
                    <li>Begrænsning af adgangen til dine oplysninger til medarbejdere og samarbejdspartnere, der har behov for dem.</li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">5. Hvem deler vi dine oplysninger med?</h3>
                  <p>
                    Vi deler kun dine personoplysninger med tredjeparter, når det er nødvendigt for at levere vores tjenester eller opfylde lovkrav:
                  </p>
                  <ol className="list-decimal pl-6 space-y-4">
                    <li>
                      <strong>Tjenesteudbydere:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>Vi samarbejder med betalingsudbydere, hosting-leverandører og fragtfirmaer for at kunne levere vores tjenester.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Myndigheder:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>Vi kan dele dine oplysninger med offentlige myndigheder, hvis det kræves ved lov, eller hvis vi har en retlig forpligtelse.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Samarbejdspartnere:</strong>
                      <ul className="list-disc pl-6 mt-2">
                        <li>Med dit samtykke kan vi dele oplysninger med markedsføringspartnere eller sociale medieplatforme.</li>
                      </ul>
                    </li>
                  </ol>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">6. Hvor længe opbevarer vi dine oplysninger?</h3>
                  <p>
                    Vi opbevarer dine personoplysninger, så længe det er nødvendigt for de formål, der er beskrevet i denne privatlivspolitik, eller så længe det kræves i henhold til lovgivningen. For eksempel:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Kontaktoplysninger opbevares i op til 1 år efter din sidste henvendelse.</li>
                    <li>Bestillings- og betalingsoplysninger opbevares i op til 5 år i henhold til bogføringsloven.</li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">7. Dine rettigheder</h3>
                  <p>Du har rettigheder i forhold til de oplysninger, vi opbevarer om dig:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Ret til indsigt:</strong> Du kan anmode om en kopi af de personoplysninger, vi opbevarer om dig.</li>
                    <li><strong>Ret til rettelse:</strong> Hvis dine oplysninger er forkerte, har du ret til at få dem rettet.</li>
                    <li><strong>Ret til sletning:</strong> Du kan bede os om at slette dine oplysninger, medmindre vi er forpligtet til at beholde dem.</li>
                    <li><strong>Ret til begrænsning:</strong> Du kan bede os om midlertidigt at begrænse behandlingen af dine data.</li>
                    <li><strong>Ret til dataportabilitet:</strong> Du kan bede om at få dine oplysninger udleveret i et struktureret, almindeligt anvendt og maskinlæsbart format.</li>
                    <li><strong>Ret til at trække samtykke tilbage:</strong> Hvis vi behandler dine oplysninger baseret på dit samtykke, kan du til enhver tid trække dette tilbage.</li>
                  </ul>
                  <p>
                    For at udøve dine rettigheder, kontakt os på <a href="mailto:brian@bennyspolsevogn.dk" className="text-red-600 hover:text-red-700">brian@bennyspolsevogn.dk</a>.
                  </p>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">8. Cookies</h3>
                  <p>Vi indsamler ikke cookies på vores hjemmeside.</p>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">9. Opdateringer til denne privatlivspolitik</h3>
                  <p>
                    Vi kan fra tid til anden opdatere denne privatlivspolitik for at afspejle ændringer i lovgivningen eller vores praksis. Eventuelle ændringer vil blive offentliggjort på denne side, og vi opfordrer dig til at gennemgå politikken regelmæssigt.
                  </p>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">10. Kontakt</h3>
                  <p>
                    Hvis du har spørgsmål, bekymringer eller klager om vores behandling af dine personoplysninger, er du velkommen til at kontakte os:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>E-mail:</strong> <a href="mailto:brian@bennyspolsevogn.dk" className="text-red-600 hover:text-red-700">brian@bennyspolsevogn.dk</a></li>
                    <li><strong>Telefon:</strong> +45 28 19 50 77</li>
                  </ul>
                  <p>Hvis du ikke er tilfreds med vores svar, kan du kontakte Datatilsynet:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Hjemmeside:</strong> <a href="http://www.datatilsynet.dk" className="text-red-600 hover:text-red-700" target="_blank" rel="noopener noreferrer">www.datatilsynet.dk</a></li>
                    <li><strong>Telefon:</strong> 33 19 32 00</li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <h3 className="text-xl font-bold">11. Information om hjemmesideudvikler</h3>
                  <p>
                    Denne hjemmeside er udviklet af Plea Tech. Hvis du har tekniske spørgsmål om hjemmesiden, kan du kontakte:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Navn:</strong> Birk Weismann Andersen</li>
                    <li><strong>Telefon:</strong> +45 71 99 35 40</li>
                    <li><strong>E-mail:</strong> <a href="mailto:birk@pleatech.io" className="text-red-600 hover:text-red-700">birk@pleatech.io</a></li>
                  </ul>

                  <div className="border-t border-gray-200" />

                  <p className="mt-8 text-center font-medium">
                    <strong>Tak fordi du besøger Benny's Pølsevogn!</strong> Vi værdsætter din tillid og arbejder hårdt for at beskytte dine data.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}